// features/procurement/GRNDetailPage.tsx
/**
 * Goods Received Note (GRN) Detail — Redesigned Executive View
 * Features:
 * - Direct confirmation modal with inventory ledger posting confirmation
 * - 1-Click "Return Items" shortcut to Purchase Return wizard
 * - Authenticated tenant-branded PDF export with live loading state
 * - Inspection scorecard: Received vs Accepted vs Rejected with rejection reason breakdown
 * - Batch and Expiry date tracking
 * - Zero silent failures with explicit notifications
 */

import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { Spinner } from '@/components/ui/Spinner';
import { ErrorState } from '@/components/ui/ErrorState';
import {
    ArrowLeft, Truck, Store, User, Calendar, CheckCircle2,
    Clock, Package, FileText, Building2, Hash,
    Printer, RotateCcw, AlertTriangle, ShieldCheck,
    Sparkles, X, AlertCircle
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';
import {
    getGRN,
    confirmGRN,
    downloadGrnPdf,
    getProcurementErrorMessage,
    GoodsReceivedNote,
} from './services/procurementApi';

const STATUS_CONFIG: Record<string, { label: string; bg: string; text: string; border: string; icon: any }> = {
    draft: { label: 'Awaiting Confirmation', bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/30', icon: Clock },
    confirmed: { label: 'Posted to Inventory', bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/30', icon: CheckCircle2 },
    disputed: { label: 'Disputed Receipt', bg: 'bg-rose-500/10', text: 'text-rose-400', border: 'border-rose-500/30', icon: AlertTriangle },
};

export function GRNDetailPage() {
    const { grnId } = useParams<{ grnId: string }>();
    const navigate = useNavigate();
    const user = useAuthStore((s) => s.user);
    const queryClient = useQueryClient();
    const addToast = useToastStore((s) => s.addToast);

    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [isExporting, setIsExporting] = useState(false);

    // ============================================================
    // QUERY
    // ============================================================

    const { data: grn, isLoading, isError, refetch } = useQuery<GoodsReceivedNote>({
        queryKey: ['grn-detail', grnId],
        queryFn: () => getGRN(user!.tenantId, grnId!),
        enabled: !!user?.tenantId && !!grnId,
    });

    // ============================================================
    // MUTATION
    // ============================================================

    const confirmMutation = useMutation({
        mutationFn: () => confirmGRN(user!.tenantId, grnId!),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['grn-detail', grnId] });
            queryClient.invalidateQueries({ queryKey: ['grns-list'] });
            queryClient.invalidateQueries({ queryKey: ['stock-levels'] });
            queryClient.invalidateQueries({ queryKey: ['dashboard-grns'] });
            setShowConfirmModal(false);
            addToast({
                type: 'success',
                title: 'GRN Confirmed & Stock Posted',
                message: `Inventory updated for ${grn?.items?.length || 0} product(s) in branch warehouse.`,
            });
        },
        onError: (err: any) => {
            setShowConfirmModal(false);
            addToast({
                type: 'error',
                title: 'Confirmation Failed',
                message: getProcurementErrorMessage(err),
            });
        },
    });

    const handleExport = async () => {
        if (!grn) return;
        setIsExporting(true);
        try {
            await downloadGrnPdf(user!.tenantId, grn.id, grn.grnNumber);
            addToast({
                type: 'success',
                title: 'PDF Export Complete',
                message: `Goods Received Note #${grn.grnNumber} saved to downloads.`,
            });
        } catch (err: any) {
            addToast({
                type: 'error',
                title: 'Export Failed',
                message: err?.message || 'Failed to download official GRN PDF document.',
            });
        } finally {
            setIsExporting(false);
        }
    };

    // ============================================================
    // HELPERS
    // ============================================================

    const formatNaira = (val: string | number | undefined) => {
        if (!val) return '₦0.00';
        const num = typeof val === 'string' ? parseFloat(val) : val;
        return isNaN(num) ? '₦0.00' : `₦${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    const formatDate = (d: string | null | undefined) => {
        if (!d) return '—';
        return new Date(d).toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' });
    };

    if (isLoading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[70vh] bg-slate-50 text-slate-600 gap-4">
                <Spinner size="lg" variant="teal" />
                <p className="text-sm font-semibold tracking-wide">Loading goods received note record...</p>
            </div>
        );
    }

    if (isError || !grn) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
                <ErrorState
                    title="GRN Document Not Found"
                    message="The requested goods received note could not be loaded or has been deleted."
                    onBack={() => navigate('/procurement/grns')}
                    onRetry={() => refetch()}
                />
            </div>
        );
    }

    const totalReceived = grn.items?.reduce((s, item) => s + parseFloat(item.quantityReceived || '0'), 0) || 0;
    const totalAccepted = grn.items?.reduce((s, item) => s + parseFloat(item.quantityAccepted || '0'), 0) || 0;
    const totalRejected = grn.items?.reduce((s, item) => s + parseFloat(item.quantityRejected || '0'), 0) || 0;
    const totalValuation = grn.items?.reduce((s, item) => {
        const qty = parseFloat(item.quantityAccepted || '0');
        const cost = parseFloat(item.unitCost || '0');
        return s + (qty * (isNaN(cost) ? 0 : cost));
    }, 0) || 0;

    const statusCfg = STATUS_CONFIG[grn.status] || STATUS_CONFIG.draft;
    const StatusIcon = statusCfg.icon;

    return (
        <div className="min-h-screen bg-slate-50/60 text-slate-900 pb-20">
            {/* Top Action Bar */}
            <div className="sticky top-0 z-30 bg-white/95 backdrop-blur-xl border-b border-slate-200 shadow-sm">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div className="flex items-center gap-3">
                            <button
                                onClick={() => navigate('/procurement/grns')}
                                className="p-2.5 rounded-xl bg-white hover:bg-slate-100 text-slate-600 hover:text-slate-900 border border-slate-200 transition-all shadow-sm"
                                type="button"
                                title="Back to GRN List"
                            >
                                <ArrowLeft className="w-5 h-5" />
                            </button>
                            <div className="p-2.5 bg-gradient-to-br from-cyan-600 to-sky-600 rounded-2xl shadow-lg shadow-cyan-600/20 text-white">
                                <Truck className="w-6 h-6" />
                            </div>
                            <div>
                                <div className="flex items-center gap-2.5 flex-wrap">
                                    <h1 className="text-xl sm:text-2xl font-black tracking-tight text-slate-900 font-mono">
                                        #{grn.grnNumber}
                                    </h1>
                                    <span className={clsx('px-3 py-1 rounded-full text-xs font-bold border flex items-center gap-1.5', statusCfg.bg, statusCfg.text, statusCfg.border)}>
                                        <StatusIcon className="w-3.5 h-3.5" />
                                        <span>{statusCfg.label}</span>
                                    </span>
                                </div>
                                <p className="text-xs text-slate-500 flex items-center gap-2 mt-0.5">
                                    <span className="font-semibold text-slate-700">{grn.vendorName || 'Supplier'}</span>
                                    <span>•</span>
                                    <span>{grn.branchName || 'Branch'}</span>
                                    <span>•</span>
                                    <span>Received {formatDate(grn.receivedAt || grn.createdAt)}</span>
                                </p>
                            </div>
                        </div>

                        {/* Top Action CTAs */}
                        <div className="flex items-center gap-3 self-end sm:self-auto flex-wrap">
                            <button
                                onClick={handleExport}
                                disabled={isExporting}
                                className="flex items-center gap-2 px-4 py-2.5 bg-white hover:bg-slate-100 border border-slate-300 text-slate-700 rounded-xl font-bold text-xs transition-all shadow-sm cursor-pointer disabled:opacity-50"
                                type="button"
                                title="Download Official GRN PDF"
                            >
                                {isExporting ? <Spinner size="xs" variant="teal" /> : <Printer className="w-4 h-4 text-slate-500" />}
                                <span>{isExporting ? 'Generating PDF...' : 'Export PDF'}</span>
                            </button>

                            {grn.status === 'confirmed' && (
                                <button
                                    onClick={() => navigate(`/procurement/returns/new?grnId=${grn.id}`)}
                                    className="flex items-center gap-2 px-4 py-2.5 bg-rose-50 hover:bg-rose-100 border border-rose-200 text-rose-700 rounded-xl font-bold text-xs transition-all cursor-pointer shadow-sm"
                                    type="button"
                                >
                                    <RotateCcw className="w-4 h-4" />
                                    <span>Return Items</span>
                                </button>
                            )}

                            {grn.status === 'draft' && (
                                <button
                                    onClick={() => setShowConfirmModal(true)}
                                    className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white rounded-xl font-bold text-xs shadow-md shadow-emerald-600/20 transition-all cursor-pointer"
                                    type="button"
                                >
                                    <CheckCircle2 className="w-4 h-4" />
                                    <span>Confirm & Post Stock</span>
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 space-y-8">
                {/* Inspection Quality Scorecard */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
                        <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider block mb-1">
                            Total Units Received
                        </span>
                        <p className="text-2xl font-black text-slate-900">{totalReceived.toLocaleString()}</p>
                        <p className="text-xs text-slate-500 mt-1">Physical dock count</p>
                    </div>

                    <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
                        <span className="text-[11px] font-bold text-emerald-700 uppercase tracking-wider block mb-1">
                            Accepted Into Stock
                        </span>
                        <p className="text-2xl font-black text-emerald-600">{totalAccepted.toLocaleString()}</p>
                        <p className="text-xs text-emerald-600 mt-1">
                            {totalReceived > 0 ? `${Math.round((totalAccepted / totalReceived) * 100)}% Pass rate` : '0%'}
                        </p>
                    </div>

                    <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
                        <span className="text-[11px] font-bold text-rose-700 uppercase tracking-wider block mb-1">
                            Rejected / Damaged
                        </span>
                        <p className={clsx('text-2xl font-black', totalRejected > 0 ? 'text-rose-600' : 'text-slate-400')}>
                            {totalRejected.toLocaleString()}
                        </p>
                        <p className="text-xs text-slate-500 mt-1">Segregated from inventory</p>
                    </div>

                    <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
                        <span className="text-[11px] font-bold text-cyan-700 uppercase tracking-wider block mb-1">
                            Accepted Valuation
                        </span>
                        <p className="text-2xl font-black text-cyan-700">{formatNaira(totalValuation)}</p>
                        <p className="text-xs text-slate-500 mt-1">Gross inventory ledger value</p>
                    </div>
                </div>

                {/* Tracking & References Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm">
                        <div className="flex items-center gap-2 text-slate-500 text-xs uppercase font-bold mb-1">
                            <Store className="w-3.5 h-3.5 text-cyan-600" /> Facility
                        </div>
                        <p className="font-bold text-slate-900 text-sm">{grn.branchName || 'Warehouse'}</p>
                    </div>

                    <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm">
                        <div className="flex items-center gap-2 text-slate-500 text-xs uppercase font-bold mb-1">
                            <FileText className="w-3.5 h-3.5 text-blue-600" /> Linked PO
                        </div>
                        <p className="font-bold text-slate-900 text-sm">
                            {grn.poNumber ? `#${grn.poNumber}` : 'Direct Inbound (No PO)'}
                        </p>
                    </div>

                    <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm">
                        <div className="flex items-center gap-2 text-slate-500 text-xs uppercase font-bold mb-1">
                            <Hash className="w-3.5 h-3.5 text-purple-600" /> Waybill / Note
                        </div>
                        <p className="font-bold text-slate-900 text-sm font-mono">{grn.deliveryNoteNumber || '—'}</p>
                    </div>

                    <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm">
                        <div className="flex items-center gap-2 text-slate-500 text-xs uppercase font-bold mb-1">
                            <User className="w-3.5 h-3.5 text-teal-600" /> Dock Receiver
                        </div>
                        <p className="font-bold text-slate-900 text-sm">{grn.receiverName || 'Warehouse Staff'}</p>
                    </div>
                </div>

                {/* Items Table */}
                <div className="bg-white border border-slate-200 rounded-3xl p-6 lg:p-8 shadow-sm space-y-6">
                    <div className="flex items-center justify-between pb-4 border-b border-slate-100">
                        <div>
                            <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                                <Package className="w-5 h-5 text-cyan-600" />
                                <span>Verified Receipt Items ({grn.items?.length || 0})</span>
                            </h3>
                            <p className="text-xs text-slate-500">Accepted vs rejected quantities, batch lots, and expiration dates</p>
                        </div>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm">
                            <thead>
                                <tr className="border-b border-slate-100 text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                                    <th className="py-3 px-4">Product Catalog</th>
                                    <th className="py-3 px-4 text-right">Received</th>
                                    <th className="py-3 px-4 text-right">Accepted</th>
                                    <th className="py-3 px-4 text-right">Rejected</th>
                                    <th className="py-3 px-4 text-right">Unit Cost</th>
                                    <th className="py-3 px-4">Batch / Lot</th>
                                    <th className="py-3 px-4">Expiry Date</th>
                                    <th className="py-3 px-4">Quality Remarks</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {grn.items?.map((item) => {
                                    const rejected = parseFloat(item.quantityRejected || '0');
                                    const accepted = parseFloat(item.quantityAccepted || '0');

                                    return (
                                        <tr key={item.id} className={clsx('hover:bg-slate-50/70 transition-colors', rejected > 0 && 'bg-rose-50/40')}>
                                            <td className="py-4 px-4">
                                                <p className="font-bold text-slate-900 text-sm">{item.productName || 'Inventory Product'}</p>
                                                <p className="text-xs text-slate-500 font-mono">UOM: {item.uomName || 'Unit'}</p>
                                            </td>
                                            <td className="py-4 px-4 text-right font-semibold text-slate-900">
                                                {parseFloat(item.quantityReceived || '0').toLocaleString()}
                                            </td>
                                            <td className="py-4 px-4 text-right font-bold text-emerald-600">
                                                {accepted.toLocaleString()}
                                            </td>
                                            <td className="py-4 px-4 text-right font-bold">
                                                {rejected > 0 ? (
                                                    <span className="text-rose-700 px-2 py-0.5 rounded-md bg-rose-50 border border-rose-200 text-xs">
                                                        {rejected.toLocaleString()}
                                                    </span>
                                                ) : (
                                                    <span className="text-slate-400">0</span>
                                                )}
                                            </td>
                                            <td className="py-4 px-4 text-right font-mono text-cyan-700 font-semibold">
                                                {formatNaira(item.unitCost)}
                                            </td>
                                            <td className="py-4 px-4 font-mono text-xs text-slate-700">
                                                {item.batchNumber ? (
                                                    <span className="px-2 py-1 bg-slate-100 border border-slate-200 rounded-lg">{item.batchNumber}</span>
                                                ) : '—'}
                                            </td>
                                            <td className="py-4 px-4 text-xs text-slate-600">
                                                {item.expiryDate ? formatDate(item.expiryDate) : '—'}
                                            </td>
                                            <td className="py-4 px-4 text-xs text-slate-500">
                                                {item.rejectionReason || item.notes || '—'}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {grn.notes && (
                        <div className="pt-4 border-t border-slate-100">
                            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider block mb-1">
                                Delivery Remarks
                            </span>
                            <p className="text-xs text-slate-700 bg-slate-50 p-3 rounded-xl border border-slate-200">
                                {grn.notes}
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {/* ========== CONFIRMATION MODAL (LIGHT BACKGROUND) ========== */}
            <AnimatePresence>
                {showConfirmModal && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
                        onClick={() => setShowConfirmModal(false)}
                    >
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0, y: 20 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.95, opacity: 0, y: 20 }}
                            onClick={(e) => e.stopPropagation()}
                            className="bg-white border border-slate-200 rounded-3xl shadow-2xl w-full max-w-md p-6 sm:p-8 text-center space-y-5"
                        >
                            <div className="w-16 h-16 rounded-2xl bg-emerald-50 text-emerald-600 border border-emerald-200 flex items-center justify-center mx-auto">
                                <CheckCircle2 className="w-8 h-8" />
                            </div>

                            <div>
                                <h2 className="text-xl font-bold text-slate-900">Post Stock to Inventory?</h2>
                                <p className="text-xs text-slate-500 mt-2">
                                    Confirming GRN #{grn.grnNumber} will immediately post <strong>{totalAccepted.toLocaleString()} accepted units</strong> into the active warehouse stock for {grn.branchName || 'your branch'}.
                                </p>
                            </div>

                            <div className="flex items-center gap-3 pt-2">
                                <button
                                    onClick={() => setShowConfirmModal(false)}
                                    className="flex-1 px-4 py-3 rounded-xl border border-slate-300 hover:bg-slate-100 text-slate-700 font-semibold text-sm transition-all shadow-sm"
                                    type="button"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => confirmMutation.mutate()}
                                    disabled={confirmMutation.isPending}
                                    className="flex-1 px-4 py-3 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 text-white hover:from-emerald-700 hover:to-teal-700 font-bold text-sm shadow-md shadow-emerald-600/20 transition-all flex items-center justify-center gap-2 cursor-pointer"
                                    type="button"
                                >
                                    {confirmMutation.isPending ? (
                                        <Spinner size="xs" variant="white" />
                                    ) : (
                                        'Confirm & Post'
                                    )}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}